/**
 * KPI 横轴事件气泡（仿 daily Engagement CardLine + useEventDetailsPop）。
 *
 * 与 daily 版本的差异：
 *   1. **监听只挂一次**（attachListenersFunc，挂在 LineChart 的 chartEventConfig 上，
 *      底层 Chart 仅在 echarts.init 时执行一次）；
 *   2. **eventDetails series 通过 appendEventDetailsSeries 在 setOtherConfigFunc 中构建**，
 *      每次 setOption 都会用最新 eventData 重新生成，因此 LineChart **不需要重挂**——
 *      这就避免了「事件数据后到 → key 变化 → unmount/mount → 闪一下空白」的问题。
 */
import { useCallback, useMemo, useRef, useState } from 'react';
import type {
  EChartsOption,
  EChartsSeries,
  TKpiEventPopDay,
  TUseKpiEventDetailsPopOptions,
} from './types';

const KPI_POP_FALLBACK_DAY: TKpiEventPopDay = {
  events: 0,
  date: '',
  event_list: [],
};

export function useEventMetricTrendKpiEventDetailsPop(
  eventData: TKpiEventPopDay[],
  popRef: React.MutableRefObject<HTMLDivElement | null>,
  options?: TUseKpiEventDetailsPopOptions,
) {
  const seriesId = options?.seriesId ?? 'eventDetails';
  const xAxisBubbleLabelColors = options?.xAxisBubbleLabelColors ?? undefined;
  const [currentIndex, setCurrentIndex] = useState(0);
  const currentIndexRef = useRef(0);
  /** 鼠标在 KPI 详情浮层 DOM 上时，不要因离开 canvas 的 globalout 立刻收起（否则与舆情浮层叠放时会闪） */
  const kpiPopSurfaceHoverRef = useRef(false);
  const currentEvents = useMemo(
    () => eventData?.[currentIndex] ?? KPI_POP_FALLBACK_DAY,
    [eventData, currentIndex],
  );

  /**
   * 仅在 echarts.init 时执行一次，挂事件监听。
   * 监听里 setCurrentIndex 触发组件重渲染，currentEvents 通过 useMemo 取最新 eventData[index]，
   * 因此即使监听闭包不更新，弹窗内容也是最新的。
   */
  const attachListenersFunc = useCallback(
    (opt: EChartsOption, chart: unknown): EChartsOption => {
      const c = chart as {
        on: (
          ev: string,
          handler: (params: {
            seriesId?: string;
            dataIndex?: number;
            event?: { offsetX: number; event: { target: HTMLElement & { width?: number; clientWidth?: number; }; }; };
          }) => void,
        ) => void;
      } | undefined;

      /** 浏览器环境 setTimeout 返回 number，与 @types/node Timeout 区分 */
      let hideAfterLeaveTimer: number | null = null;
      const clearHideTimer = (): void => {
        if (hideAfterLeaveTimer !== null) {
          clearTimeout(hideAfterLeaveTimer);
          hideAfterLeaveTimer = null;
        }
      };
      const scheduleHideAfterLeaveCanvas = (): void => {
        clearHideTimer();
        hideAfterLeaveTimer = window.setTimeout(() => {
          hideAfterLeaveTimer = null;
          if (kpiPopSurfaceHoverRef.current) {
            return;
          }
          if (popRef.current) {
            popRef.current.style.visibility = 'hidden';
            popRef.current.style.opacity = '0';
          }
        }, 80);
      };

      c?.on('mouseover', (params) => {
        if (params.seriesId !== seriesId) {
          return;
        }
        /**
         * 关键：clearHideTimer 必须在「已显示则跳过位置重算」之前调用。
         * 否则当用户离开 chart（schedule 了 80ms hide timer）后 80ms 内
         * 再次 hover 同一气泡，会因 opacity===1 提前 return 跳过 clearHideTimer，
         * 80ms 后 timer 仍会把浮窗隐藏，表现为「hover 在气泡上但浮窗没出来」。
         */
        clearHideTimer();
        if (popRef.current && Number(popRef.current.style.opacity) === 1) {
          return;
        }
        const idx = params?.dataIndex ?? 0;
        setCurrentIndex(idx);
        currentIndexRef.current = idx;
        const evt = params.event;
        if (!evt || !popRef.current) return;
        const { offsetX } = evt;
        const target = evt.event.target as HTMLElement & {
          width?: number;
          clientWidth?: number;
        };
        const chartWidth = target.clientWidth || target.width || 0;
        const halfPopWidth = 380 / 2;
        const left =
          offsetX > chartWidth - halfPopWidth
            ? chartWidth - 380
            : offsetX < halfPopWidth
              ? 0
              : offsetX - halfPopWidth;
        popRef.current.style.visibility = 'visible';
        popRef.current.style.opacity = '1';
        popRef.current.style.transform = `translate3d(${left}px, 0px, 0px)`;
      });

      c?.on('mousemove', (params) => {
        if (params.seriesId !== seriesId) {
          return;
        }
        // 同 mouseover：鼠标还在 eventDetails 上移动，永远取消 pending 的隐藏 timer
        clearHideTimer();
        if (params?.dataIndex === currentIndexRef.current) {
          return;
        }
        const idx = params?.dataIndex ?? 0;
        setCurrentIndex(idx);
        currentIndexRef.current = idx;
      });

      c?.on('mouseout', scheduleHideAfterLeaveCanvas);

      c?.on('globalout', scheduleHideAfterLeaveCanvas);

      const tip = opt?.tooltip as { extraCssText?: string; } | undefined;
      if (tip?.extraCssText) {
        tip.extraCssText = 'z-index: 101';
      }

      return opt;
    },
    [popRef, seriesId],
  );

  /**
   * 在 setOtherConfigFunc 内调用：用最新 eventData 拼出 eventDetails series + 双 yAxis；
   * 与 daily Engagement CardLine.tsx 的 eventSlotConfigFunc 等价（仅顺序略调整以适配本组件 setOtherConfigFunc 的执行时机）。
   */
  const appendEventDetailsSeries = useCallback(
    (option: EChartsOption): EChartsOption => {
      const seriesData =
        eventData?.map(({ events }, index) => {
          let width = 14;
          let text: string | number = events;
          if (events >= 99) {
            width = 32;
            text = '99+';
          } else if (events > 10 && events < 99) {
            width = 20;
          } else if (events > 0) {
            width = 20;
          }
          return {
            name: index,
            value: 0,
            label: {
              show: events > 0,
              width,
              formatter: () => String(text),
            },
          };
        }) || [];

      const eventDetailsSeries: EChartsSeries = {
        id: seriesId,
        name: '__kpi_event_details__',
        type: 'line',
        showSymbol: true,
        showAllSymbol: false,
        data: seriesData,
        tooltip: { show: false },
        symbolSize: 0,
        itemStyle: { color: 'rgba(0, 0, 0, 0)' },
        label: {
          show: true,
          backgroundColor: xAxisBubbleLabelColors?.background ?? '#fff',
          borderWidth: 1,
          borderColor: xAxisBubbleLabelColors?.borderColor ?? '#efeff2',
          fontSize: 8,
          fontWeight: 500,
          color: xAxisBubbleLabelColors?.fontColor ?? '#9B9FB0',
          position: 'inside',
          verticalAlign: 'middle',
          width: 20,
          height: 14,
          borderRadius: 5,
        },
        emphasis: {
          disabled: true,
          scale: false,
          label: {
            color: xAxisBubbleLabelColors?.emphasisFontColor ?? '#fff',
            backgroundColor:
              xAxisBubbleLabelColors?.emphasisBackground ?? '#0076FF',
            borderColor:
              xAxisBubbleLabelColors?.emphasisBorderColor ??
              xAxisBubbleLabelColors?.emphasisBackground ??
              '#0076FF',
          },
        },
        /** 高于默认折线（zlevel 0），避免主系列盖住 X 轴数字气泡导致 hover 难命中 */
        zlevel: 2,
        z: 100,
        yAxisIndex: 1,
      } as unknown as EChartsSeries;

      option.series = [...((option.series as EChartsSeries[]) || []), eventDetailsSeries];

      // 双 yAxis：第一条主 y 轴保留，第二条隐藏（与 daily eventSlotConfigFunc 等价）
      const baseYAxis = Array.isArray(option.yAxis)
        ? option.yAxis
        : option.yAxis
          ? [option.yAxis]
          : [];
      option.yAxis = [
        ...baseYAxis,
        {
          show: false,
          axisLabel: {
            formatter() {
              return '';
            },
          },
        },
      ] as EChartsOption['yAxis'];

      const {grid} = option;
      if (grid && typeof grid === 'object' && !Array.isArray(grid)) {
        const nextGrid: Record<string, unknown> = {
          ...(grid as Record<string, unknown>),
          x2: 2,
        };
        option.grid = nextGrid as EChartsOption['grid'];
      }

      return option;
    },
    [eventData, seriesId, xAxisBubbleLabelColors],
  );

  return {
    currentEvents,
    attachListenersFunc,
    appendEventDetailsSeries,
    kpiPopSurfaceHoverRef,
  };
}
